// Originally this was going to be a subclass of ApiRepo, but the logic around user
// files is very different -- involving three-way communication between the client,
// the server, and S3 -- and I decided it was simpler to build from scratch.

import {api2Endpoint} from 'app/config/apiConfig'
import {IUserFile} from 'app/models/user_file'
import {CUserFile} from 'app/modules/discovery/core'
import {queryClient, queryDefaults} from 'app/react-query'
import {useMutation, useQuery} from 'react-query'
import {isJsonData, jsonDataIsArrayWithIds} from 'utilities/types'
import {deleteData} from './api/useDeleteData'
import {getData} from './api/useGetData'
import {postData} from './api/usePostData'
import {announceReturn, announceSend} from './api/utils/announce'
import errorNotify from './api/utils/errorNotify'
import {postUserFileToS3} from './user_files/postUserFileToS3'

export const RawDataFileRepo = {
  timestamp: 0,
  useCreate() {
    return useMutation(async (args: {file: File; tableName: string}) => {
      const {file, tableName} = args
      // First, post to the create method on the iScout API. This DOES NOT upload
      // the file. Instead, it notifies iScout that the file is coming, and
      // retrieves an S3 URL we can use for the actual upload.
      const route = `std/raw_data/${tableName}`
      const body = {name: file.name.split(' ').join('_')}
      const method = 'POST'
      announceSend(method, route, body)
      // TEST Evan probably needs to review this
      const userFile = await postData<IUserFile>(api2Endpoint(route), body)
      announceReturn(method, route, body)
      await postUserFileToS3(userFile, file)
      queryClient.invalidateQueries(['raw_data_files', tableName])
    })
  },
  useUpdate() {},
  useDelete() {
    return useMutation(async (args: {fileId: string; tableName: string}) => {
      const {fileId, tableName} = args
      const route = `std/raw_data/${tableName}/${fileId}`
      const method = 'DELETE'
      announceSend(method, route)
      // TEST Evan probably needs to review this
      await deleteData<IUserFile>(api2Endpoint(route))
      announceReturn(method, route)
      queryClient.invalidateQueries(['raw_data_files', tableName])
    })
  },
  useList(args: {tableName: string}) {
    const {tableName} = args
    return useQuery({
      queryKey: ['raw_data_files', tableName],
      queryFn: async () => {
        if (tableName === undefined) {
          return undefined
        }
        try {
          this.timestamp = Date.now()
          const route = `std/raw_data/${tableName}`
          announceSend('GET', route)
          // TEST Evan probably needs to review this
          const response = await getData<unknown>(api2Endpoint(route))
          announceReturn('GET', route)
          if (!isJsonData(response) || !jsonDataIsArrayWithIds(response)) {
            throw new Error(
              `Expected ${route} to return array of JSON-serializable objects with ids, got: ${JSON.stringify(
                response
              )}`
            )
          }
          return response.map((x) => new CUserFile(x))
        } catch (err) {
          errorNotify(err, `Failed to retrieve raw data files for "${tableName}".`)
        } finally {
          this.timestamp = Date.now()
        }
      },
      select: (data) => data,
      enabled: true,
      cacheTime: queryDefaults.cacheTime,
      staleTime: queryDefaults.staleTime,
    })
  },
}
